import { Suspense, lazy, type ComponentType } from 'react'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { Manifest } from '../api/authoring'
import { typeOf } from '../model/manifest'
import { FormEditor } from './FormEditor'
import { GalleryEditor } from './GalleryEditor'
import { GenericEditor } from './GenericEditor'
import { GridEditor } from './GridEditor'
import { PatternEditor } from './PatternEditor'
import type { EditorProps } from './types'

// Editors are **presentation**, keyed by type name, and this map is now
// an exception list rather than the mechanism.
//
// Everything structural — what may be placed where, what regions exist,
// what intents may be named — comes off the manifest, and since it began
// carrying a schema per type and per intent, so does the *form*. A type
// this client has never heard of gets real controls, prefilled from its
// stored fields and sending one whole payload per intent, with no change
// here.
//
// **`PageFieldsEditor` is gone, and that deletion is the evidence.** It
// hand-wrote a title box and an SEO pair; `page.retitled` and
// `page.seo_set` are plain field intents, so the generated form is the
// same form, down to the button that reads "Set seo" where the hand
// written one read "Set metadata". A hand-written editor now has to earn
// its place by doing something a schema cannot describe.
//
// **`ContainerEditor` went the same way, and it is the sharper case.** It
// claimed its place for "its placement list" — and once the composition
// control read every node's arrangement off the fields schema, that list
// was drawn on the tree where it belongs and the editor was three
// controls over `container.presentation_set`. The generated form is not
// merely equal there, it is *more correct*: `height` is a closed
// vocabulary and the hand-written box let an author type into it, and the
// hand-written spacing list had been copied from the Go slice and had
// since lost `xs`, so one legal value could not be chosen at all. That is
// the failure mode a second declaration of a closed set has, found in the
// one place this client still had one.
//
// What each survivor still earns:
//   Content — a rich-text editor over markup a schema cannot describe.
//             A body is authored HTML, and the one thing that mattered
//             turned out to be what an editor does with markup it does
//             *not* understand: see `docs/research/wysiwyg.md`.
//   Gallery — a thumbnail grid. Every intent it declares is structural,
//             which is exactly why the generated form is empty and this
//             one earns its place: a schema can say a gallery holds an
//             ordered list of link ids, and cannot say that each one is
//             a picture worth looking at. It is also the type that made
//             this urgent — a photographer's site could not be authored
//             here at all.
//   Grid    — a cell arranger. Its cells carry a span and an alignment
//             *per placement*, and `grid.cell_configured` names the
//             placement it configures, so the control has to sit with the
//             tree rather than in a form over the type's own fields.
//   Form    — a field builder. Its inputs are *named* rather than linked,
//             so they trip no placement marker and the composition
//             control correctly does not draw them; `form.fields_reordered`
//             carries a bare list of those names, which is the one intent
//             the split has always reported as having no control. A list
//             of records keyed by a member the schema cannot point at is
//             precisely what a derived control cannot know.
//   Pattern and Repeater — a properties form typed by *another document*.
//             Their `props` are maps, so the manifest's schema for them
//             says `object`; what may be in one is declared by the pattern
//             it runs, which is chosen per placement. That is the one
//             shape a schema on the type cannot describe, and the
//             declaration publishes the form instead.
//   the type
//   designer — the same argument as Form, applied to the type whose
//             documents *are* type definitions: a declaration is two
//             lists of records, and the generated form draws scalars.
//   the pattern
//   designer — the same argument again, and the strongest case of it. A
//             pattern declaration is two lists of records *and* a
//             recursive tree, and a tree is not a form at any depth —
//             `SchemaForm`'s cap of two levels is the right answer for a
//             presentation with a background inside it and the wrong
//             shape of answer entirely for something that contains
//             itself. Under the generic editor a declaration drew its
//             root node's link id and type and silently omitted its
//             children, its fields and its bindings.
//
// Neither designer is keyed by a type name, because this client does not
// hold either meta-type's name — the manifest says which type declares
// types and which declares patterns, exactly as it says which type holds
// a file.
//
// **Content is loaded lazily, and it is the first editor that had to
// be.** TinyMCE is around 370 KB gzipped for one registered type of
// nine, and the common session edits a routing tree and never opens a
// Content at all. The boundary is here rather than at each call site so
// that neither screen has to know which editors are heavy — the same
// shape as the platform's own lazy provider resolution.
const LazyContent = lazy(async () => ({
  default: (await import('./ContentEditor')).ContentEditor,
}))

function ContentEditor(props: EditorProps) {
  return (
    <Suspense fallback={<EditorLoading />}>
      <LazyContent {...props} />
    </Suspense>
  )
}

function EditorLoading() {
  return (
    <Stack spacing={1}>
      <LinearProgress />
      <Typography variant="caption" color="text.secondary">
        Loading the editor…
      </Typography>
    </Stack>
  )
}

// The type designer is lazy for the reason Content is: it is a large
// control that most sessions never open, and neither screen should have
// to know which editors are heavy.
const LazyTypeEditor = lazy(async () => ({
  default: (await import('./TypeEditor')).TypeEditor,
}))

function TypeEditor(props: EditorProps) {
  return (
    <Suspense fallback={<EditorLoading />}>
      <LazyTypeEditor {...props} />
    </Suspense>
  )
}

/** The pattern designer is lazy for the reason the type designer is. */
const LazyPatternDesigner = lazy(async () => ({
  default: (await import('./PatternDesigner')).PatternDesigner,
}))

function PatternDesigner(props: EditorProps) {
  return (
    <Suspense fallback={<EditorLoading />}>
      <LazyPatternDesigner {...props} />
    </Suspense>
  )
}

const editors: Record<string, ComponentType<EditorProps>> = {
  Content: ContentEditor,
  Form: FormEditor,
  Gallery: GalleryEditor,
  Grid: GridEditor,
  Pattern: PatternEditor,
  Repeater: PatternEditor,
}

/**
 * The editor for one type.
 *
 * The manifest is consulted **before** the name map, and for two
 * questions: does this type declare component types, and does it declare
 * patterns? Those are the two meta-types, and each is answered by asking
 * rather than by holding its name — the same rule the uploader and the
 * palette follow, and the reason `"ComponentPattern"` appears nowhere in
 * this client. Everything else is the exception list, keyed by name
 * because those editors are about types this build ships.
 */
export function editorFor(typeName: string, manifest?: Manifest): ComponentType<EditorProps> {
  const declared = typeOf(manifest, typeName)
  if (declared?.defines) return TypeEditor
  if (declared?.declaresPatterns) return PatternDesigner
  return editors[typeName] ?? GenericEditor
}

export { GenericEditor }
export type { EditorProps }
